import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import {
  PageWrap,
  Header,
  HeaderLeft,
  HeaderCenter,
  HeaderRight,
  Title,
  Subtitle,
  Ornament,
  OrnLine,
  OrnDiamond,
  Badge,
  FiltersBar,
  SearchInput,
  FilterSelect,
  ProductsGrid,
  ProductCard,
  ProductHeader,
  ProductTitleWrap,
  ProductName,
  ProductCategory,
  ProductStatus,
  ProductBody,
  ProductImageWrap,
  ProductImage,
  ProductImageFallback,
  StockRow,
  StockLabel,
  StockInput,
  ToggleRow,
  ToggleLabel,
  ToggleSwitch,
  ToggleThumb,
  InfoText,
  EmptyState,
  InfoBox,
  ForbiddenWrap,
  MessageBox,
  MessageIcon,
  MessageContent,
  MessageTitle,
  MessageText,
  MessageClose,
  ClosePageButton,
  BackButton,
  HeaderBottom,
} from "./EmployeeStockPageStyles";

const CATEGORY_ORDER = [
  "cadouri_dulci",
  "cafea_ceai",
  "desert_pahar",
  "patiserie",
  "platouri_evenimente",
  "platouri_post",
  "prajituri_artizanale",
  "tasting_box",
  "torturi",
];

const CATEGORY_LABELS = {
  cadouri_dulci: "Cadouri Dulci",
  cafea_ceai: "Cafea & Ceai",
  desert_pahar: "Desert la Pahar",
  patiserie: "Patiserie",
  platouri_evenimente: "Platouri Evenimente",
  platouri_post: "Platouri de Post",
  prajituri_artizanale: "Prăjituri Artizanale",
  tasting_box: "Tasting Box",
  torturi: "Torturi",
};

const GET_EMPLOYEE_PRODUCTS = gql`
  query GetEmployeeProducts {
    adminProducts {
      id
      categorie
      nume
      imagine
      pret
      ordine
      stoc
      disponibil
      tip_pret
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($id: ID!, $input: ProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      stoc
      disponibil
    }
  }
`;

function getStoredUser() {
  const possibleKeys = ["user", "authUser", "currentUser", "ahava_user"];

  for (const key of possibleKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed) return parsed;
    } catch {
      //
    }
  }

  return null;
}

export default function EmployeeStockPage() {
  const navigate = useNavigate();
  const messageRef = useRef(null);

  const [hasAccess, setHasAccess] = useState(false);
  const [checkedAuth, setCheckedAuth] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("toate");
  const [statusFilter, setStatusFilter] = useState("toate");

  const [editedProducts, setEditedProducts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const user = getStoredUser();
    const role = user?.role;

    if (user && role === "angajat") {
      setHasAccess(true);
    } else {
      setHasAccess(false);
    }

    setCheckedAuth(true);
  }, []);

  const { data, loading, error, refetch } = useQuery(GET_EMPLOYEE_PRODUCTS, {
    skip: !checkedAuth || !hasAccess,
    fetchPolicy: "network-only",
  });

  const [updateProduct] = useMutation(UPDATE_PRODUCT);

  const products = useMemo(() => data?.adminProducts ?? [], [data]);

  const scrollToMessage = () => {
    setTimeout(() => {
      if (!messageRef.current) return;

      const y =
        messageRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        120;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 50);
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (categoryFilter !== "toate") {
      list = list.filter((p) => p.categorie === categoryFilter);
    }

    if (statusFilter === "active") {
      list = list.filter((p) => p.disponibil === true);
    }

    if (statusFilter === "inactive") {
      list = list.filter((p) => p.disponibil === false);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          String(p.nume || "")
            .toLowerCase()
            .includes(q) ||
          String(p.categorie || "")
            .toLowerCase()
            .includes(q),
      );
    }

    return list.sort((a, b) => {
      const catA = CATEGORY_ORDER.indexOf(a.categorie);
      const catB = CATEGORY_ORDER.indexOf(b.categorie);

      if (catA !== catB) return catA - catB;

      const ordA = Number(a.ordine ?? 0);
      const ordB = Number(b.ordine ?? 0);
      if (ordA !== ordB) return ordA - ordB;

      return String(a.nume || "").localeCompare(String(b.nume || ""));
    });
  }, [products, categoryFilter, statusFilter, search]);

  const getDraft = (product) => {
    return (
      editedProducts[product.id] || {
        stoc: product.stoc ?? 0,
        disponibil: !!product.disponibil,
      }
    );
  };

  const handleDraftChange = (productId, field, value, baseProduct = null) => {
    setEditedProducts((prev) => {
      const current = prev[productId] || {
        stoc: baseProduct?.stoc ?? 0,
        disponibil: !!baseProduct?.disponibil,
      };

      const next = {
        ...current,
        [field]: value,
      };

      if (field === "disponibil" && value === false) {
        next.stoc = 0;
      }

      return {
        ...prev,
        [productId]: next,
      };
    });
  };

  const persistProduct = async (product, nextDraft) => {
    const stocValue = nextDraft.disponibil ? Number(nextDraft.stoc) : 0;

    if (nextDraft.disponibil && (Number.isNaN(stocValue) || stocValue < 0)) {
      setMessage({
        type: "error",
        text: `Stoc invalid pentru produsul „${product.nume}”.`,
      });
      scrollToMessage();
      return false;
    }

    try {
      setSavingId(product.id);

      await updateProduct({
        variables: {
          id: product.id,
          input: {
            categorie: product.categorie,
            nume: product.nume,
            imagine: product.imagine || null,
            pret: Number(product.pret ?? 0),
            ordine: Number(product.ordine ?? 0),
            tip_pret: product.tip_pret || "fix",
            stoc: stocValue,
            disponibil: !!nextDraft.disponibil,
          },
        },
      });

      await refetch();

      setEditedProducts((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });

      setMessage({
        type: "success",
        text: `Produsul „${product.nume}” a fost actualizat.`,
      });
      scrollToMessage();

      return true;
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || `Nu am putut salva produsul „${product.nume}”.`,
      });
      scrollToMessage();
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleQuickToggle = async (product) => {
    const currentDraft = getDraft(product);

    const nextDraft = {
      ...currentDraft,
      disponibil: !currentDraft.disponibil,
      stoc: currentDraft.disponibil
        ? 0
        : currentDraft.stoc || product.stoc || 0,
    };

    setEditedProducts((prev) => ({
      ...prev,
      [product.id]: nextDraft,
    }));

    await persistProduct(product, nextDraft);
  };

  const handleStockBlur = async (product) => {
    const draft = getDraft(product);
    await persistProduct(product, draft);
  };

  const totalProducts = products.length;
  const totalVisible = filteredProducts.length;

  if (!checkedAuth) {
    return (
      <PageWrap>
        <InfoBox>Se verifică accesul...</InfoBox>
      </PageWrap>
    );
  }

  if (!hasAccess) {
    return (
      <ForbiddenWrap>
        <h2>Acces restricționat</h2>
        <p>Această pagină este disponibilă doar pentru angajați.</p>
        <BackButton type="button" onClick={() => navigate("/")}>
          Înapoi la site
        </BackButton>
      </ForbiddenWrap>
    );
  }

  return (
    <PageWrap>
      <ClosePageButton type="button" onClick={() => navigate("/")}>
        ×
      </ClosePageButton>

      <Header>
        <HeaderCenter>
          <Title>Gestionare disponibilitate produse</Title>

          <Ornament>
            <OrnLine />
            <OrnDiamond />
          </Ornament>

          <Subtitle>
            Pagina pentru angajați: modifici rapid stocul și disponibilitatea,
            direct din cardurile produselor.
          </Subtitle>
        </HeaderCenter>

        <HeaderBottom>
          <HeaderLeft>
            <Badge>{totalProducts} produse</Badge>
          </HeaderLeft>

          <HeaderRight />
        </HeaderBottom>
      </Header>

      <FiltersBar>
        <SearchInput
          type="text"
          placeholder="Caută produs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <FilterSelect
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="toate">Toate categoriile</option>
          {CATEGORY_ORDER.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat] || cat}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="toate">Toate statusurile</option>
          <option value="active">Disponibile</option>
          <option value="inactive">Indisponibile</option>
        </FilterSelect>
      </FiltersBar>

      <InfoText>
        Se afișează {totalVisible} din {totalProducts} produse.
      </InfoText>

      {message.text ? (
        <MessageBox ref={messageRef} $type={message.type}>
          <MessageIcon $type={message.type}>
            {message.type === "error" ? "!" : "✓"}
          </MessageIcon>

          <MessageContent>
            <MessageTitle>
              {message.type === "error" ? "Atenție" : "Succes"}
            </MessageTitle>
            <MessageText>{message.text}</MessageText>
          </MessageContent>

          <MessageClose
            type="button"
            onClick={() => setMessage({ type: "", text: "" })}
          >
            ×
          </MessageClose>
        </MessageBox>
      ) : null}

      {loading ? (
        <InfoBox>Se încarcă produsele...</InfoBox>
      ) : error ? (
        <InfoBox>Eroare la încărcare. Verifică backend-ul.</InfoBox>
      ) : filteredProducts.length === 0 ? (
        <EmptyState>Nu există produse pentru filtrele alese.</EmptyState>
      ) : (
        <ProductsGrid>
          {filteredProducts.map((product) => {
            const draft = getDraft(product);
            const isSavingThis = String(savingId) === String(product.id);

            return (
              <ProductCard key={product.id} $available={!!draft.disponibil}>
                <ProductHeader>
                  <ProductTitleWrap>
                    <ProductName>{product.nume}</ProductName>
                    <ProductCategory>
                      {CATEGORY_LABELS[product.categorie] || product.categorie}
                    </ProductCategory>
                  </ProductTitleWrap>

                  <ProductStatus $ok={!!draft.disponibil}>
                    {draft.disponibil ? "Disponibil" : "Indisponibil"}
                  </ProductStatus>
                </ProductHeader>

                <ProductImageWrap>
                  {product.imagine ? (
                    <ProductImage
                      src={product.imagine}
                      alt={product.nume}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}

                  <ProductImageFallback
                    style={{ display: product.imagine ? "none" : "flex" }}
                  >
                    Fără poză
                  </ProductImageFallback>
                </ProductImageWrap>

                <ProductBody>
                  <ToggleRow>
                    <ToggleLabel>
                      {isSavingThis
                        ? "Se salvează..."
                        : "Disponibil pentru comandă"}
                    </ToggleLabel>

                    <ToggleSwitch
                      type="button"
                      $active={!!draft.disponibil}
                      disabled={isSavingThis}
                      onClick={() => handleQuickToggle(product)}
                    >
                      <ToggleThumb $active={!!draft.disponibil} />
                    </ToggleSwitch>
                  </ToggleRow>

                  <StockRow>
                    <StockLabel>
                      {draft.disponibil ? "Stoc produs" : "Stoc blocat"}
                    </StockLabel>

                    <StockInput
                      type="number"
                      min="0"
                      value={draft.stoc}
                      disabled={!draft.disponibil || isSavingThis}
                      onChange={(e) =>
                        handleDraftChange(
                          product.id,
                          "stoc",
                          e.target.value,
                          product,
                        )
                      }
                      onBlur={() => handleStockBlur(product)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleStockBlur(product);
                        }
                      }}
                    />
                  </StockRow>
                </ProductBody>
              </ProductCard>
            );
          })}
        </ProductsGrid>
      )}
    </PageWrap>
  );
}
